import { createBrowserRouter, redirect, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { useParams, Outlet } from "react-router-dom";

import PublicLayout from "../ui/layouts/public.layout";
import PrivateLayout from "~/ui/layouts/private.layout";
import ErrorPage from "../pages/error-page/ui/error.page";
import { LoginPage } from "~/pages/auth/login/ui/login.page";
import { LoginOtpPage } from "~/pages/auth/login-otp/ui/login-otp.page";
import { RegisterInvitePage } from "~/pages/auth/register-invite/ui/register-invite.page";
import { ForgotPasswordForm as ForgotPasswordPage } from "~/features/forgot-password/ui/forgot-password-form-admin";

import CustomersPage from "~/pages/dashboard/customers/ui/customers.page";
import UsersPage from "~/pages/dashboard/users/ui/users.page";
import AutoparkPage from "~/pages/dashboard/autopark/ui/autopark.page";
import ShiftSchedulePage from "~/pages/dashboard/shift-schedule/ui/shift-schedule.page";
import { DashboardPage } from "~/pages/dashboard/dashboard/ui/dashboard.page";
import OrdersPage from "~/pages/dashboard/orders/ui/orders.page";
import { RoutesPage } from "~/pages/dashboard/routes/ui/routes.page";
import { PickupLocationsPage } from "~/pages/dashboard/pickup-locations/ui/pickup-locations.page";
import { PickupLocationGroupsPage } from "~/pages/dashboard/pickup-location-groups/ui/pickup-location-groups.page";
import AnalyticsPage from "~/pages/dashboard/analytics/ui/analytics.page";
import SettingsPage from "~/pages/settings/ui/settings.page";
import { ChatPage } from "~/pages/dashboard/chat/ui/chat.page";
import ServiceProvidersPage from "~/pages/dashboard/service-providers/ui/service-providers.page";
import ContractsPage from "~/pages/dashboard/contracts/ui/contracts.page";
import GpsTrackingPage from "~/pages/dashboard/gps-tracking/ui/gps-tracking.page";
import { WeightManagementAdminPage } from "~/pages/weight-management/ui/weight-management-admin-page";
import PlatformUsersPage from "~/pages/dashboard/user-settings/ui/user-settings";
import PlatformOrganizationsPage from "~/pages/dashboard/organizations-admin/ui/organization-admin";
import ChangePasswordPage from "~/pages/settings/components/change-password-platform.page";
import InventoryPage from "~/pages/dashboard/inventory/ui/inventory.page";

import { AUTH_STORAGE } from "@racoon/lib";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { COMPANY_STORAGE_KEY } from "~/shared/store/company/company.slice";

function getAuthToken(): string | null {
  try {
    return localStorage.getItem("auth_token");
  } catch {
    return null;
  }
}

function getUser() {
  try {
    return JSON.parse(localStorage.getItem(AUTH_STORAGE.user) || "{}");
  } catch {
    return {};
  }
}

function getPersistedCompanyId(): number | null {
  try {
    const raw = localStorage.getItem(COMPANY_STORAGE_KEY);
    if (!raw) return null;

    // If user is admin, ignore saved company to redirect to admin panel
    const user = getUser();
    if (user.role === "USER_ROLE_ADMIN") return null;

    const parsed = JSON.parse(raw) as { selectedCompanyId?: number | null };
    return parsed?.selectedCompanyId ?? null;
  } catch {
    return null;
  }
}

/**
 * Главная логика распределения пользователей после входа
 */
function getHomePathForUser(): string {
  const user = getUser();

  if (user.role === "USER_ROLE_ADMIN") {
    return "/organization-admin";
  }

  const userOrgId = user.owner_organization_id || user.ownerOrganizationId || user.organization_id;

  const storedOrgId = getPersistedCompanyId();
  const targetId = storedOrgId || userOrgId;

  if (targetId) {
    return `/${targetId}/dashboard`;
  }

  return "/login";
}
async function rootLoader() {
  const token = getAuthToken();

  if (!token) return redirect("/login");

  // If token exists - calculate correct path
  const path = getHomePathForUser();
  return redirect(path);
}

async function redirectIfAuthed() {
  const token = getAuthToken();
  if (!token) return null;

  const path = getHomePathForUser();

  if (path === "/login") {
    return null;
  }

  return redirect(path);
}

async function requireAuth() {
  const token = getAuthToken();
  if (!token) return redirect("/login");
  return null;
}

async function requireCompany({ params }: { params: { companyId?: string } }) {
  if (!params.companyId) {
    // If somehow ended up here without ID, try to return home
    const path = getHomePathForUser();
    return redirect(path);
  }
  return null;
}

function CompanyParamSync() {
  const { companyId } = useParams();
  const { selectedCompanyId, selectById } = useSelectedCompany();

  useEffect(() => {
    if (companyId) {
      const id = Number(companyId);
      if (!Number.isNaN(id) && id !== selectedCompanyId) {
        selectById(id);
      }
    }
  }, [companyId, selectedCompanyId, selectById]);

  return null;
}

function CompanyLayout() {
  return (
    <>
      <CompanyParamSync />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    loader: rootLoader,
    errorElement: <ErrorPage />,
  },

  // unauthenticated routes
  {
    element: <PublicLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/login",
        loader: redirectIfAuthed,
        element: <LoginPage />,
      },
      {
        path: "/login-otp",
        loader: redirectIfAuthed,
        element: <LoginOtpPage />,
      },
      {
        path: "/forgot-password",
        loader: redirectIfAuthed,
        element: <ForgotPasswordPage />,
      },
      {
        path: "/register-invite",
        element: <RegisterInvitePage />,
      },
    ],
  },

  // authenticated routes
  {
    element: <PrivateLayout />,
    loader: requireAuth,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/organization-admin",
        element: <PlatformOrganizationsPage />,
      },
      {
        path: "/platform-users",
        element: <PlatformUsersPage />,
      },
      {
        path: "/change-password",
        element: <ChangePasswordPage />,
      },
      {
        path: "/:companyId",
        element: <CompanyLayout />,
        loader: requireCompany,
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: "customers", element: <CustomersPage /> },
          { path: "employees", element: <UsersPage /> },
          { path: "autopark", element: <AutoparkPage /> },
          { path: "shift-schedule", element: <ShiftSchedulePage /> },
          { path: "dashboard", element: <DashboardPage /> },
          { path: "orders", element: <OrdersPage /> },
          { path: "routes", element: <RoutesPage /> },
          { path: "weight-management", element: <WeightManagementAdminPage /> },
          { path: "pickup-locations", element: <PickupLocationsPage /> },
          { path: "pickup-location-groups", element: <PickupLocationGroupsPage /> },
          { path: "analytics", element: <AnalyticsPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "chat", element: <ChatPage /> },
          { path: "service-providers", element: <ServiceProvidersPage /> },
          { path: "contracts", element: <ContractsPage /> },
          { path: "gps-tracking", element: <GpsTrackingPage /> },
          { path: "inventory", element: <InventoryPage /> },
        ],
      },
    ],
  },

  { path: "*", element: <ErrorPage /> },
]);
